import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { translations, SUPPORTED_LANGUAGE_CODES } from './translations';

/**
 * Minimal, dependency-free i18n for the public application form.
 *
 * Why not react-i18next: this codebase is a plain Create React App project
 * with no TypeScript and no existing i18n setup anywhere, and the actual
 * requirement is "translate one page" (the /apply form), including a couple
 * of messages that are generated outside of any component (the per-step
 * required-field checks in PublicApplicationForm.js). Pulling in a full i18n
 * framework (namespaces, suspense loading, plural rules, backend-loaded
 * bundles) for that is more machinery than the problem calls for, and it is
 * one more dependency + version/peer-dep surface to maintain.
 *
 * That said, this is written to be easy to swap out later if this grows
 * past one page:
 *   - useTranslation() returns { t, language, setLanguage } - same names/
 *     shape as react-i18next's hook.
 *   - t(key, vars) takes a dot-path key and does {{var}} interpolation,
 *     matching react-i18next's interpolation syntax.
 *   - translations.js keys are already flat per-language objects, i.e. the
 *     same shape as react-i18next resource bundles, so they could become
 *     en.json/es.json/fr.json/pt.json largely unchanged.
 *   - getTranslator(language) below (used by the non-component validation
 *     logic) mirrors how react-i18next exposes a standalone i18n.t() outside
 *     of React components/hooks.
 *
 * Migrating later would mean swapping this file's internals for
 * react-i18next's provider/hook and converting translations.js into JSON
 * resource files - call sites that already do `t('some.key')` should not
 * need to change.
 */

const STORAGE_KEY = 'applyFormLanguage';

const LANGUAGE_NAMES = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  pt: 'Português'
};

export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGE_CODES.map((code) => ({
  code,
  name: LANGUAGE_NAMES[code] || code
}));

const DEFAULT_LANGUAGE = 'en';

function isSupported(code) {
  return SUPPORTED_LANGUAGE_CODES.includes(code);
}

function detectInitialLanguage() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // localStorage can throw in some privacy modes/embedded contexts - fall
    // through to the browser-language guess below.
  }

  // Best-effort guess from the browser so an applicant whose OS/browser is
  // already set to Spanish/French/Portuguese sees that language on first
  // visit, without needing to find the dropdown. Still just a starting
  // point - they can change it with the selector regardless.
  try {
    const nav = (navigator.languages && navigator.languages[0]) || navigator.language || '';
    const base = nav.slice(0, 2).toLowerCase();
    if (isSupported(base)) return base;
  } catch {
    // ignore
  }

  return DEFAULT_LANGUAGE;
}

/** Reads a dot-path (e.g. "errors.fields.phone") out of a nested object. */
function resolveKey(obj, key) {
  return key.split('.').reduce((acc, part) => (acc && typeof acc === 'object' ? acc[part] : undefined), obj);
}

/** Replaces {{varName}} placeholders in a template string. */
function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  ));
}

/**
 * Standalone translator for a given language, usable outside of React
 * components (e.g. in getStepError(), which is plain function logic, not a
 * component). Falls back to English, then to the key itself, so a missing
 * translation never renders as a blank field.
 */
export function getTranslator(language) {
  const lang = isSupported(language) ? language : DEFAULT_LANGUAGE;
  return function t(key, vars) {
    const value =
      resolveKey(translations[lang], key) ??
      resolveKey(translations[DEFAULT_LANGUAGE], key) ??
      key;
    return typeof value === 'string' ? interpolate(value, vars) : value;
  };
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(detectInitialLanguage);

  const setLanguage = useCallback((code) => {
    if (!isSupported(code)) return;
    setLanguageState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Non-fatal - language choice just won't persist across a reload.
    }
  }, []);

  const t = useMemo(() => getTranslator(language), [language]);

  const value = useMemo(() => ({ t, language, setLanguage }), [t, language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Named to match react-i18next's useTranslation() so component call sites
 * (`const { t } = useTranslation();`) would not need to change if this is
 * ever swapped for the real library.
 */
export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return ctx;
}
